import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./UserPanel/Sidebar";
import ModalSchedules from "./Schedules/ModalSchedules";

const WEEKDAYS = [
  { value: 1, label: "Lunes" },
  { value: 2, label: "Martes" },
  { value: 3, label: "Miércoles" },
  { value: 4, label: "Jueves" },
  { value: 5, label: "Viernes" },
  { value: 6, label: "Sábado" },
  { value: 7, label: "Domingo" },
];

const EMPTY_FORM = {
  id: null,
  name: "",
  description: "",
  is_active: true,
  ranges: [],
};

function getApiMessage(data, fallback) {
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.detail === "string") return data.detail;
  if (data?.errors && typeof data.errors === "object") return Object.values(data.errors).flat(Infinity).join(" ");
  return fallback;
}

function formatDate(value) {
  if (!value) return "Sin fecha";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin fecha";
  return new Intl.DateTimeFormat("es-EC", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function getWeekdayName(value) {
  return WEEKDAYS.find((weekday) => weekday.value === Number(value))?.label || "Día";
}

function normalizeRange(range) {
  return {
    weekday: Number(range.weekday),
    start_time: String(range.start_time || "").slice(0, 5),
    end_time: String(range.end_time || "").slice(0, 5),
    is_active: range.is_active !== false,
  };
}

export default function Schedules() {
  const { fetchUser } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [detailSchedule, setDetailSchedule] = useState(null);
  const [deleteSchedule, setDeleteSchedule] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [feedback, setFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showFeedback = useCallback((severity, message) => {
    setFeedback({ open: true, severity, message });
  }, []);

  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [organizationResponse, schedulesResponse] = await Promise.all([
        fetchUser("/tenants/api/logo/", {
          method: "GET",
          cache: "no-store",
        }),
        fetchUser("/access-control/api/web/schedules/", {
          method: "GET",
          cache: "no-store",
        }),
      ]);
      const organizationData = await readJson(organizationResponse);
      const schedulesData = await readJson(schedulesResponse);
      if (!organizationResponse.ok || organizationData.ok !== true || !organizationData.tenant) {
        throw new Error(getApiMessage(organizationData, "No se pudo cargar la organización."));
      }
      if (!schedulesResponse.ok || schedulesData.ok !== true) {
        throw new Error(getApiMessage(schedulesData, "No se pudieron cargar los horarios."));
      }
      setOrganization(organizationData.tenant);
      setSchedules(Array.isArray(schedulesData.schedules) ? schedulesData.schedules : []);
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error ? error.message : "No se pudo conectar con el servidor."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchUser, showFeedback]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredSchedules = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return schedules;
    return schedules.filter((schedule) => {
      return (
        String(schedule.name || "").toLowerCase().includes(query) ||
        String(schedule.description || "").toLowerCase().includes(query)
      );
    });
  }, [schedules, search]);

  const paginatedSchedules = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredSchedules.slice(start, start + rowsPerPage);
  }, [filteredSchedules, page, rowsPerPage]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredSchedules.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredSchedules.length, page, rowsPerPage]);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, ranges: [] });
    setFormOpen(true);
  };

  const openEdit = (schedule) => {
    setForm({
      id: schedule.id,
      name: schedule.name || "",
      description: schedule.description || "",
      is_active: schedule.is_active === true,
      ranges:
        Array.isArray(schedule.ranges) && schedule.ranges.length > 0
          ? schedule.ranges.map(normalizeRange)
          : [],
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setForm({ ...EMPTY_FORM, ranges: [] });
  };

  const validateForm = () => {
    if (!form.name.trim()) {
      showFeedback("error", "El nombre del horario es obligatorio.");
      return false;
    }
    if (!Array.isArray(form.ranges) || form.ranges.length === 0) {
      showFeedback("error", "Debes registrar al menos un rango.");
      return false;
    }
    for (const range of form.ranges) {
      if (!range.start_time || !range.end_time) {
        showFeedback("error", "Todos los rangos deben tener hora inicial y final.");
        return false;
      }
      if (range.start_time >= range.end_time) {
        showFeedback(
          "error",
          `La hora final de ${getWeekdayName(range.weekday)} debe ser mayor que la inicial.`
        );
        return false;
      }
    }
    for (let index = 0; index < form.ranges.length; index += 1) {
      const current = form.ranges[index];
      for (let compareIndex = index + 1; compareIndex < form.ranges.length; compareIndex += 1) {
        const compared = form.ranges[compareIndex];
        if (Number(current.weekday) !== Number(compared.weekday)) continue;
        const overlaps =
          current.start_time < compared.end_time &&
          current.end_time > compared.start_time;
        if (overlaps) {
          showFeedback(
            "error",
            `Existen rangos cruzados el ${getWeekdayName(current.weekday).toLowerCase()}.`
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const editing = Boolean(form.id);
      const response = await fetchUser(
        editing
          ? `/access-control/api/web/schedules/${form.id}/`
          : "/access-control/api/web/schedules/",
        {
          method: editing ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: form.name.trim(),
            description: form.description.trim(),
            is_active: form.is_active,
            ranges: form.ranges.map((range) => ({
              weekday: Number(range.weekday),
              start_time: range.start_time,
              end_time: range.end_time,
              is_active: range.is_active !== false,
            })),
          }),
        }
      );
      const data = await readJson(response);
      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(
            data,
            editing ? "No se pudo actualizar el horario." : "No se pudo crear el horario."
          )
        );
      }
      setFormOpen(false);
      setForm({ ...EMPTY_FORM, ranges: [] });
      showFeedback(
        "success",
        data.message ||
          (editing ? "Horario actualizado correctamente." : "Horario creado correctamente.")
      );
      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error ? error.message : "No se pudo guardar el horario."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (schedule) => {
    setUpdatingId(schedule.id);
    try {
      const response = await fetchUser(
        `/access-control/api/web/schedules/${schedule.id}/`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: !schedule.is_active }),
        }
      );
      const data = await readJson(response);
      if (!response.ok || data.ok !== true) {
        throw new Error(getApiMessage(data, "No se pudo cambiar el estado del horario."));
      }
      showFeedback("success", data.message || "Estado actualizado correctamente.");
      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error ? error.message : "No se pudo cambiar el estado del horario."
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteSchedule) return;
    setDeletingId(deleteSchedule.id);
    try {
      const response = await fetchUser(
        `/access-control/api/web/schedules/${deleteSchedule.id}/`,
        { method: "DELETE" }
      );
      const data = await readJson(response);
      if (!response.ok || data.ok === false) {
        throw new Error(getApiMessage(data, "No se pudo eliminar el horario."));
      }
      setDeleteSchedule(null);
      showFeedback("success", data.message || "Horario eliminado correctamente.");
      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error ? error.message : "No se pudo eliminar el horario."
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !organization) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        organization={organization}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1200, margin: "0 auto" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, gap: 1 }}>
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{ display: { xs: "inline-flex", md: "none" } }}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "#111111",
                    fontSize: { xs: 26, md: 32 },
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  Horarios
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 14,
                    fontWeight: 500,
                    marginTop: 0.5,
                  }}
                >
                  {schedules.length} horarios registrados
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={openCreate}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minHeight: 44,
                borderBottom: "3px solid #b00020",
              }}
            >
              Nuevo horario
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #e1e1e4",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                padding: { xs: 2, sm: 2.5 },
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Buscar por nombre o descripción"
              />
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Horario</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell>Rangos</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Creación</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedSchedules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Box
                            sx={{
                              minHeight: 220,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography sx={{ fontWeight: 800 }}>
                              No hay horarios
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedSchedules.map((schedule) => (
                        <TableRow key={schedule.id} hover>
                          <TableCell>
                            <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                              {schedule.name}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {schedule.description || "Sin descripción"}
                          </TableCell>

                          <TableCell>
                            {schedule.ranges_count ?? schedule.ranges?.length ?? 0}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={schedule.is_active ? "Activo" : "Inactivo"}
                              sx={{
                                fontWeight: 700,
                                color: schedule.is_active ? "#047857" : "#b00020",
                                backgroundColor: schedule.is_active ? "#ecfdf5" : "#fff1f2",
                              }}
                            />
                          </TableCell>

                          <TableCell>{formatDate(schedule.created_at)}</TableCell>

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDetailSchedule(schedule)}
                              >
                                Ver
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openEdit(schedule)}
                              >
                                Editar
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                disabled={updatingId === schedule.id}
                                onClick={() => handleToggleStatus(schedule)}
                              >
                                {updatingId === schedule.id
                                  ? "Procesando"
                                  : schedule.is_active
                                    ? "Desactivar"
                                    : "Activar"}
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => setDeleteSchedule(schedule)}
                              >
                                Eliminar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={{ display: { xs: "block", md: "none" }, padding: 2 }}>
              {paginatedSchedules.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    No hay horarios
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {paginatedSchedules.map((schedule) => (
                    <Box
                      key={schedule.id}
                      sx={{
                        padding: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          marginBottom: 1.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 800,
                            wordBreak: "break-word",
                          }}
                        >
                          {schedule.name}
                        </Typography>

                        <Chip
                          size="small"
                          label={schedule.is_active ? "Activo" : "Inactivo"}
                          sx={{
                            flexShrink: 0,
                            fontWeight: 700,
                            color: schedule.is_active ? "#047857" : "#b00020",
                            backgroundColor: schedule.is_active ? "#ecfdf5" : "#fff1f2",
                          }}
                        />
                      </Box>

                      <Typography
                        sx={{
                          color: "#6b7280",
                          fontSize: 13,
                          lineHeight: 1.5,
                          marginBottom: 1.5,
                        }}
                      >
                        {schedule.description || "Sin descripción"}
                      </Typography>

                      <Typography sx={{ fontSize: 13, marginBottom: 1.5 }}>
                        <strong>Rangos:</strong>{" "}
                        {schedule.ranges_count ?? schedule.ranges?.length ?? 0}
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDetailSchedule(schedule)}
                        >
                          Ver
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(schedule)}
                        >
                          Editar
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          disabled={updatingId === schedule.id}
                          onClick={() => handleToggleStatus(schedule)}
                        >
                          {updatingId === schedule.id
                            ? "Procesando"
                            : schedule.is_active
                              ? "Desactivar"
                              : "Activar"}
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => setDeleteSchedule(schedule)}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <TablePagination
              component="div"
              count={filteredSchedules.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Filas"
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                },
              }}
            />
          </Paper>
        </Box>
      </Box>

      <ModalSchedules
        open={formOpen}
        form={form}
        setForm={setForm}
        saving={saving}
        weekdays={WEEKDAYS}
        onClose={closeForm}
        onSave={handleSave}
        showFeedback={showFeedback}
      />

      <Dialog
        open={Boolean(detailSchedule)}
        onClose={() => setDetailSchedule(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Información del horario</DialogTitle>

        <DialogContent>
          {detailSchedule && (
            <Stack spacing={2} sx={{ paddingTop: 1 }}>
              <Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Nombre
                </Typography>

                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 800,
                    marginTop: 0.3,
                  }}
                >
                  {detailSchedule.name}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Descripción
                </Typography>

                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    marginTop: 0.3,
                  }}
                >
                  {detailSchedule.description || "Sin descripción"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 12,
                    fontWeight: 700,
                    marginBottom: 1,
                  }}
                >
                  Rangos
                </Typography>

                <Stack spacing={1}>
                  {(detailSchedule.ranges || []).length === 0 ? (
                    <Typography sx={{ fontSize: 14 }}>
                      Sin rangos registrados
                    </Typography>
                  ) : (
                    detailSchedule.ranges.map((range, index) => (
                      <Box
                        key={range.id || `${range.weekday}-${range.start_time}-${index}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          padding: 1.2,
                          border: "1px solid #e5e7eb",
                          borderRadius: 2,
                        }}
                      >
                        <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                          {range.weekday_name || getWeekdayName(range.weekday)}
                        </Typography>

                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                          {String(range.start_time || "").slice(0, 5)} -{" "}
                          {String(range.end_time || "").slice(0, 5)}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Estado
                </Typography>

                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    marginTop: 0.3,
                  }}
                >
                  {detailSchedule.is_active ? "Activo" : "Inactivo"}
                </Typography>
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Fecha de creación
                </Typography>

                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    marginTop: 0.3,
                  }}
                >
                  {formatDate(detailSchedule.created_at)}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailSchedule(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteSchedule)}
        onClose={() => {
          if (!deletingId) setDeleteSchedule(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar horario</DialogTitle>

        <DialogContent>
          <Typography>
            ¿Deseas eliminar el horario {deleteSchedule?.name}?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteSchedule(null)}
            disabled={Boolean(deletingId)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={Boolean(deletingId)}
          >
            {deletingId ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() =>
          setFeedback((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() =>
            setFeedback((previous) => ({
              ...previous,
              open: false,
            }))
          }
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}